
import { LogManager, Navigation } from "@mcleod/core";
import { RouteChangedEvent, Router } from "../../../common/src/router/Router";
import { MainPageAccessor } from "../base/MainPageAccessor";
import { Layout } from "../components/layout/Layout";
import { LayoutProps } from "../components/layout/LayoutProps";

const log = LogManager.getLogger("components.page.LayoutTabLauncher");
const LAYOUT_LOAD_TIMEOUT = 20000;

export class LayoutTabLauncher {

    /**
     * Opens a layout in a new browser tab and returns the loaded layout.
     *
     * @param path - The layout path to open
     * @param layoutProps - Optional props to pass to the layout
     * @returns Promise that resolves to the Layout instance in the new browser tab
     */
    public static async open(path: string, layoutProps?: Partial<LayoutProps>): Promise<Layout> {
        try {
            const windowName = `${path}-${Date.now()}`;
            const layoutPromise = this.getLayoutFromNewWindow(windowName, layoutProps);
            await Navigation.navigateTo(path, {
                newTab: true,
                title: windowName
            });
            return await layoutPromise;
        } catch (error) {
            log.error("Failed to open layout in new tab:", error);
            throw error;
        }
    }

    private static async getLayoutFromNewWindow(windowName: string, layoutProps?: Partial<LayoutProps>): Promise<Layout> {
        const thisRouter = MainPageAccessor.router as Router;

        if (thisRouter == null) {
            return Promise.reject(new Error("Router not available"));
        }

        return new Promise((resolve, reject) => {

            const timeout = setTimeout(() => {
                thisRouter.removeChildRouteConfig(windowName);
                reject(new Error("Timeout waiting for layout to load"));
            }, LAYOUT_LOAD_TIMEOUT);

            thisRouter.addChildRouteConfig(windowName, {
                routeChangeListener: async (event: RouteChangedEvent) => {
                    const layout = event.layout;
                    try {
                        clearTimeout(timeout);
                        await layout.ensureLoaded();
                        resolve(layout);
                    } catch (error) {
                        clearTimeout(timeout);
                        reject(error);
                    }
                },
                layoutPropsSupplier: layoutProps
            });
        });
    }
}
